import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { SubscriptionBanner } from './SubscriptionBanner.js';
import { CapWarningBanner } from './CapWarningBanner.js';
import { UserMenu, type NavGroup } from './UserMenu.js';
import { NavSidebar } from './NavSidebar.js';

/**
 * Shell for authenticated pages. Top nav (brand + user menu) plus a left
 * sidebar with the main sections grouped under headers. Content area is a flex
 * column that consumes the remaining viewport.
 *
 * Belt-and-suspenders: the authedLayoutRoute beforeLoad already bounces
 * employees to /me by reading the JWT role claim. The useEffect below is
 * a runtime backstop — if for any reason a user with role=employee
 * reaches this layout (stale browser bundle, corrupt JWT, AuthContext
 * hydrating after beforeLoad ran), we bounce them on first render. The
 * brief flash is preferable to silently showing them admin chrome.
 */
export function AuthedLayout({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'employee') {
      void navigate({ to: '/me' });
    }
  }, [user?.role, navigate]);

  async function handleLogout(): Promise<void> {
    await logout();
    await navigate({ to: '/login' });
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="border-b border-gray-900 px-6 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="font-serif text-xl">
          LumoHR
        </Link>
        <div className="flex items-center gap-4 text-sm">
          {user ? (
            <UserMenu user={user} onLogout={handleLogout} />
          ) : (
            // Defensive: AuthedLayout always has a user, but keep a sign-out path.
            <button
              type="button"
              onClick={handleLogout}
              className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
            >
              Sign out
            </button>
          )}
        </div>
      </header>
      <SubscriptionBanner mode="admin" />
      <CapWarningBanner />
      <div className="flex flex-1 min-h-0">
        {/* ADR-0025 D8 — the IT-scoped shell: an `it` custodian sees ONLY the
            Assets surface + their own /me (never the HR/pay/leave nav they'd be
            403'd from anyway). The API gates are the real wall; this is
            convenience-in-depth. */}
        <NavSidebar
          groups={user?.role === 'it' ? IT_NAV_GROUPS : adminNavGroups(user?.role)}
          ariaLabel="Main"
          storageKey={user?.role === 'it' ? 'lumohr.nav.it' : 'lumohr.nav.admin'}
        />
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — five logical groups. Security lives in the header UserMenu, not
// here (it's per-user account management, not tenant configuration).
// ---------------------------------------------------------------------------

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'People',
    items: [
      { label: 'Dashboard', to: '/dashboard' },
      { label: 'Employees', to: '/employees' },
      { label: 'Intl. remote workers', to: '/international-remote-roster' },
    ],
  },
  {
    label: 'Pay',
    items: [
      { label: 'Payroll runs', to: '/payroll-runs' },
      { label: 'Loans', to: '/loans' },
      { label: 'Loan requests', to: '/loans/requests' },
      // ADR-0020 expense management. Nav shows regardless of tier (entitlement
      // gating is ADR-0017, not built) — like EWA/recruitment.
      { label: 'Expense approvals', to: '/expense/approvals' },
      { label: 'Expense portfolio', to: '/expense/portfolio' },
      { label: 'FX rates', to: '/fx-rates' },
    ],
  },
  {
    label: 'Leave',
    items: [
      { label: 'Leave approvals', to: '/leave/approvals' },
      { label: 'Leave admin', to: '/leave/admin' },
      { label: 'Leave policies', to: '/leave-policies' },
    ],
  },
  {
    label: 'Attendance',
    items: [
      { label: 'Timesheets', to: '/attendance/timesheets' },
      { label: 'Punch-log import', to: '/attendance/import' },
      { label: 'Manual clock', to: '/attendance/manual-clock' },
      { label: 'Config & review', to: '/attendance/config' },
      { label: 'Overtime approvals', to: '/attendance/overtime/approvals' },
    ],
  },
  {
    label: 'Performance',
    items: [
      { label: 'Administration', to: '/performance/admin' },
      { label: 'Goal cascade', to: '/performance/goals' },
      { label: 'Reviews to complete', to: '/performance/reviews' },
      { label: 'Roll-up', to: '/performance/rollup' },
    ],
  },
  {
    label: 'Recruitment',
    items: [
      { label: 'Requisitions', to: '/recruitment/requisitions' },
      { label: 'Approvals', to: '/recruitment/approvals' },
      { label: 'Candidates', to: '/recruitment/candidates' },
      { label: 'Import candidates', to: '/recruitment/import' },
      { label: 'Retention & erasure', to: '/recruitment/retention' },
    ],
  },
  {
    // ADR-0024 Asset Management. Nav shows regardless of tier (asset_management
    // is declared-not-wired — no requirePlan gates these routes).
    label: 'Assets',
    items: [
      { label: 'Catalog', to: '/assets/catalog' },
      { label: 'Requisitions', to: '/assets/requisitions' },
      { label: 'Categories', to: '/assets/categories' },
      { label: 'Bulk import', to: '/assets/import' },
    ],
  },
  {
    // ADR-0027/0028 Advanced Reports — the Growth management-reporting layer. Kept
    // DISTINCT from Compliance (statutory filings, all tiers): different thing,
    // different place. Gated on `advanced_reports` at the API (a Starter tenant
    // reading a report gets the upgrade prompt — the nav is the upgrade lever).
    //
    // Role visibility: Headcount + Payroll cost are owner/admin/hr/finance —
    // exactly the non-`it` nav roles (employee is redirected to /me), so every
    // role that sees this nav can read both; no role wart, no filtering. Leave
    // liability is owner/admin/finance only (ADR-0028 D10, HR excluded), so HR
    // would get a 403 dead end (not a 402 upgrade prompt) — it is injected
    // per-role in `adminNavGroups` and hidden from HR. (Note: the "cost report
    // wart" from ADR-0027 — HR seeing a link it's 403'd on — no longer exists;
    // it went away when #335 added HR to the cost report.)
    label: 'Reports',
    items: [
      { label: 'Headcount & turnover', to: '/reports/headcount' },
      { label: 'Payroll cost', to: '/reports/payroll-cost' },
      // Absence is owner/admin/hr/finance — every non-`it` nav role — so it needs
      // no per-role filtering (unlike Leave liability); it lives in the static set.
      { label: 'Absence', to: '/reports/absence' },
      // Cost to company is also owner/admin/hr/finance → static, no filtering.
      { label: 'Cost to company', to: '/reports/cost-to-company' },
    ],
  },
  {
    label: 'Compliance',
    items: [{ label: 'Compliance reports', to: '/compliance-reports' }],
  },
  {
    label: 'Settings',
    items: [{ label: 'Bank profile', to: '/bank-profile' }],
  },
];

/**
 * The admin/HR nav, with the Leave-liability report injected into Reports only for
 * roles that can actually read it (owner/admin/finance — ADR-0028 D10). HR sees
 * the other two reports but not this one (it would 403). Convenience-in-depth; the
 * API gate is the real wall.
 */
const LIABILITY_ROLES = new Set(['owner', 'admin', 'finance']);

/**
 * ADR-0034 — the data export is owner/admin only (narrower than Billing, which
 * also admits finance), so its Settings link is INJECTED per role rather than
 * living in the static set.
 *
 * ⚠️ Deliberately NOT the Bank-profile pattern beside it. Bank profile is
 * "owner/admin only" by convention with its link shown to everyone, so HR clicks
 * it and gets a 403 — a control the user can see and cannot use. Repeating that
 * on the single most sensitive action in the product would be worse than a dead
 * link: it advertises the whole-payroll export to roles that must not have it.
 *
 * This is convenience-in-depth, not the gate. `requireRole('owner','admin')` on
 * the route is the wall (EXP-GATE-1); hiding a link protects nobody by itself.
 */
const EXPORT_ROLES = new Set(['owner', 'admin']);

export function adminNavGroups(role: string | undefined): NavGroup[] {
  const withLiability =
    role && LIABILITY_ROLES.has(role)
      ? NAV_GROUPS.map((g) =>
          g.label === 'Reports'
            ? { ...g, items: [...g.items, { label: 'Leave liability', to: '/reports/leave-liability' }] }
            : g,
        )
      : NAV_GROUPS;

  if (!role || !EXPORT_ROLES.has(role)) return withLiability;
  return withLiability.map((g) =>
    g.label === 'Settings'
      ? { ...g, items: [...g.items, { label: 'Data export', to: '/data-export' }] }
      : g,
  );
}

// ADR-0025 D8 — the `it` custodian's nav: a Dashboard link (the asset-only
// dashboard, /dashboard branches by role) + the Assets group + their own /me.
export const IT_NAV_GROUPS: NavGroup[] = [
  { label: 'Overview', items: [{ label: 'Dashboard', to: '/dashboard' }] },
  ...NAV_GROUPS.filter((g) => g.label === 'Assets'),
  {
    label: 'Me',
    items: [
      { label: 'My assets', to: '/me/assets' },
      { label: 'My profile', to: '/me/profile' },
    ],
  },
];
